<script setup lang="ts">
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { useI18n } from 'vue-i18n';
import { type RouteLocationRaw, RouterLink } from 'vue-router';

import AgentCard from '@/components/AgentCard/AgentCard.vue';
import type { AgentProfile } from '@/types/agentProfile';

defineProps<{
  agents: AgentProfile[];
  locationLabel: string;
  seeAllRoute?: RouteLocationRaw;
  language: HgSupportedLanguage;
}>();

const emit = defineEmits<{
  agentCardClick: [agent: AgentProfile, index: number];
}>();

const { t } = useI18n();
</script>

<template>
  <div :class="$style.parentSuggestions">
    <div :class="$style.parentSuggestionsHeader">
      <h3 :class="$style.parentSuggestionsTitle">
        {{
          t('agentList.parentSuggestionsTitle', {
            location: locationLabel,
          })
        }}
      </h3>
      <RouterLink v-if="seeAllRoute" :to="seeAllRoute" :class="$style.parentSuggestionsSeeAll">
        {{
          t('agentList.parentSuggestionsSeeAll', {
            location: locationLabel,
          })
        }}
      </RouterLink>
    </div>
    <div :class="$style.agentList">
      <AgentCard
        v-for="(agent, index) in agents"
        :key="`parent-${agent.agentId}`"
        :agent="agent"
        :language="language"
        @agentCardClick="emit('agentCardClick', agent, index)"
      />
    </div>
  </div>
</template>

<style module lang="scss">
@use '@/assets/variables' as *;

.parentSuggestions {
  display: flex;
  flex-direction: column;
  gap: $space-150;
  margin-top: $space-200;
}

.parentSuggestionsHeader {
  display: flex;
  flex-wrap: wrap;
  gap: $space-50;
  align-items: baseline;
  justify-content: space-between;
}

.parentSuggestionsTitle {
  margin: 0;

  @include heading-2;
}

.parentSuggestionsSeeAll {
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
}

.agentList {
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-150;
}
</style>
